import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  ContractsListWrapper,
  ContractsListTitleSection,
  ContractsListTitleInner,
  ContractsListPageTitle,
  ContractsListPageSubtitle,
  ContractsListHeaderButton,
  ContractsListContent,
  SearchAndFiltersWrapper,
  SearchInputWrapper,
  SearchInput,
  FiltersContainer,
  FilterButton,
  TableWrapper,
  Table,
  TableHead,
  TableHeadRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  NameText,
  ContractTypeBadge,
  StatusBadge,
  PaginationWrapper,
  PaginationInfo,
  PaginationControls,
  RowsPerPage,
  PaginationButtons,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
} from "./Styles";
import { AiOutlineFileText } from "react-icons/ai";
import { FiSearch, FiChevronLeft, FiChevronRight, FiChevronDown, FiArchive, FiRefreshCw } from "react-icons/fi";
import { MdRemoveRedEye } from "react-icons/md";
import { useContracts } from "../../../Common/Hooks/useContracts";
import { ContractSummary, ContractStatus, CONTRACT_STATUS_ACTIVE, CONTRACT_STATUS_ARCHIVED } from "../../../Common/Types-DTOs/Contracts";
import { isErr } from "../../../Common/Utils/Result";
import { eventBus } from "../../../Common/EventBus";
import { Notification } from "../../../Components/Notifications";
import { formatAbbreviatedDate } from "../../../Common/Utils/dateUtils";
import { Button } from "../../../Components/Button";
import { Popup } from "../../../Components/Popup";
import { Modal } from "../../../Components/Modal";
import { ProcessingMessage } from "../../../Components/ProcessingMessage";
import { NewContractModal } from "./NewContractModal";

type SortField = "title" | "templateName" | "createdAt" | null;
type SortDirection = "asc" | "desc";

export const ContractsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const getStatusLabel = (status: string) =>
    status === CONTRACT_STATUS_ARCHIVED
      ? t("contracts.contractsList.statusLabels.archived")
      : t("contracts.contractsList.statusLabels.active");
  const { listContracts, updateContractMetadata } = useContracts();
  const [contracts, setContracts] = useState<ContractSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewContractModal, setShowNewContractModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [contractToArchive, setContractToArchive] = useState<{ id: string; title: string; nextStatus: ContractStatus } | null>(null);

  const loadContracts = async (includeArchived: boolean) => {
    setIsLoading(true);
    const result = await listContracts({ includeArchived });
    if (isErr(result)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("contracts.contractsList.notifications.loadFailed"))
      );
      setIsLoading(false);
      return;
    }
    setContracts(result.value.contracts);
    setIsLoading(false);
  };

  useEffect(() => {
    loadContracts(showArchived);
  }, [showArchived]);

  const filteredContracts = useMemo(() => {
    let filtered = [...contracts];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (contract) =>
          contract.title.toLowerCase().includes(query) ||
          contract.templateName.toLowerCase().includes(query)
      );
    }

    if (sortField) {
      filtered.sort((a, b) => {
        let aValue: string | number;
        let bValue: string | number;

        switch (sortField) {
          case "title":
            aValue = a.title.toLowerCase();
            bValue = b.title.toLowerCase();
            break;
          case "templateName":
            aValue = a.templateName.toLowerCase();
            bValue = b.templateName.toLowerCase();
            break;
          case "createdAt":
            aValue = new Date(a.createdAt).getTime();
            bValue = new Date(b.createdAt).getTime();
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [contracts, searchQuery, sortField, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(filteredContracts.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedContracts = filteredContracts.slice(startIndex, endIndex);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleConfirmArchiveToggle = async () => {
    if (!contractToArchive) return;
    const result = await updateContractMetadata(contractToArchive.id, { status: contractToArchive.nextStatus });

    if (isErr(result)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification(
          "error",
          contractToArchive.nextStatus === CONTRACT_STATUS_ARCHIVED
            ? t("contracts.contractsList.notifications.archiveFailed")
            : t("contracts.contractsList.notifications.restoreFailed")
        )
      );
      setContractToArchive(null);
      return;
    }

    eventBus.emit(
      "NOTIFICATION",
      Notification.buildNotification(
        "success",
        contractToArchive.nextStatus === CONTRACT_STATUS_ARCHIVED
          ? t("contracts.contractsList.notifications.archiveSuccess", { title: contractToArchive.title })
          : t("contracts.contractsList.notifications.restoreSuccess", { title: contractToArchive.title })
      )
    );
    setContractToArchive(null);
    await loadContracts(showArchived);
  };

  const renderSortIcon = (field: SortField) => (
    <span className="sort-icon" style={{ fontSize: "1rem", opacity: sortField === field ? 1 : 0.4 }}>
      {sortField === field ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  return (
    <ContractsListWrapper>
      <ContractsListTitleSection>
        <ContractsListTitleInner>
          <ContractsListPageTitle>{t("contracts.contractsList.pageTitle")}</ContractsListPageTitle>
          <ContractsListPageSubtitle>
            {t("contracts.contractsList.pageSubtitle")}
          </ContractsListPageSubtitle>
        </ContractsListTitleInner>
        <ContractsListHeaderButton>
          <Button onClick={() => setShowNewContractModal(true)}>
            <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>+</span>
            {t("contracts.contractsList.newContractButton")}
          </Button>
        </ContractsListHeaderButton>
      </ContractsListTitleSection>

      <ContractsListContent>
        {isLoading ? (
          <EmptyState>
            <ProcessingMessage text={t("contracts.contractsList.loadingContracts")} />
          </EmptyState>
        ) : contracts.length > 0 ? (
          <>
            <SearchAndFiltersWrapper>
              <SearchInputWrapper>
                <FiSearch className="search-icon" size={20} />
                <SearchInput
                  type="text"
                  placeholder={t("contracts.contractsList.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </SearchInputWrapper>
              <FiltersContainer>
                <FilterButton
                  $active={showArchived}
                  onClick={() => {
                    setShowArchived((prev) => !prev);
                    setCurrentPage(1);
                  }}
                >
                  <FiArchive size={14} />
                  {showArchived
                    ? t("contracts.contractsList.filters.hideArchived")
                    : t("contracts.contractsList.filters.showArchived")}
                </FilterButton>
              </FiltersContainer>
            </SearchAndFiltersWrapper>

            <TableWrapper>
              <Table>
                <TableHead>
                  <TableHeadRow>
                    <TableHeaderCell className="sortable" onClick={() => handleSort("title")}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        {t("contracts.contractsList.table.columns.title")}
                        {renderSortIcon("title")}
                      </div>
                    </TableHeaderCell>
                    <TableHeaderCell className="sortable" onClick={() => handleSort("templateName")}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        {t("contracts.contractsList.table.columns.template")}
                        {renderSortIcon("templateName")}
                      </div>
                    </TableHeaderCell>
                    <TableHeaderCell className="sortable" onClick={() => handleSort("createdAt")}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        {t("contracts.contractsList.table.columns.date")}
                        {renderSortIcon("createdAt")}
                      </div>
                    </TableHeaderCell>
                    {showArchived && <TableHeaderCell>{t("contracts.contractsList.table.columns.status")}</TableHeaderCell>}
                    <TableHeaderCell className="actions"></TableHeaderCell>
                  </TableHeadRow>
                </TableHead>
                <TableBody>
                  {paginatedContracts.map((contract) => (
                    <TableRow key={contract.id} onClick={() => navigate(`/contracts/${contract.id}`)}>
                      <TableCell>
                        <NameText>{contract.title}</NameText>
                      </TableCell>
                      <TableCell>
                        <ContractTypeBadge>{contract.templateName}</ContractTypeBadge>
                      </TableCell>
                      <TableCell>{formatAbbreviatedDate(contract.createdAt)}</TableCell>
                      {showArchived && (
                        <TableCell>
                          <StatusBadge $status={contract.status}>{getStatusLabel(contract.status)}</StatusBadge>
                        </TableCell>
                      )}
                      <TableCell className="actions" onClick={(e) => e.stopPropagation()}>
                        <Popup
                          options={[
                            {
                              icon: <MdRemoveRedEye />,
                              text: t("contracts.contractsList.rowActions.viewDetails"),
                              onClick: () => navigate(`/contracts/${contract.id}`),
                            },
                            contract.status === CONTRACT_STATUS_ACTIVE
                              ? {
                                  icon: <FiArchive />,
                                  text: t("contracts.contractsList.rowActions.archive"),
                                  color: "#ef4444",
                                  onClick: () =>
                                    setContractToArchive({ id: contract.id, title: contract.title, nextStatus: CONTRACT_STATUS_ARCHIVED }),
                                }
                              : {
                                  icon: <FiRefreshCw />,
                                  text: t("contracts.contractsList.rowActions.unarchive"),
                                  color: "#3B82F6",
                                  onClick: () =>
                                    setContractToArchive({ id: contract.id, title: contract.title, nextStatus: CONTRACT_STATUS_ACTIVE }),
                                },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableWrapper>

            <PaginationWrapper>
              <PaginationInfo>
                <Trans
                  i18nKey="contracts.contractsList.pagination.info"
                  values={{
                    start: filteredContracts.length === 0 ? 0 : startIndex + 1,
                    end: Math.min(endIndex, filteredContracts.length),
                    total: filteredContracts.length,
                  }}
                  components={{
                    1: <span className="number" />,
                    2: <span className="number" />,
                    3: <span className="number" />,
                  }}
                />
              </PaginationInfo>
              <PaginationControls>
                <RowsPerPage>
                  <span>{t("contracts.contractsList.pagination.rowsPerPageLabel")}</span>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                    <FiChevronDown
                      size={16}
                      style={{
                        position: 'absolute',
                        right: '0.5rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        color: '#6B7280',
                      }}
                    />
                  </div>
                </RowsPerPage>
                <PaginationButtons>
                  <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
                    <FiChevronLeft />
                  </button>
                  <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
                    <FiChevronRight />
                  </button>
                </PaginationButtons>
              </PaginationControls>
            </PaginationWrapper>
          </>
        ) : (
          <EmptyState>
            <EmptyStateIcon>
              <AiOutlineFileText />
            </EmptyStateIcon>
            <EmptyStateTitle>{t("contracts.contractsList.emptyState.title")}</EmptyStateTitle>
            <EmptyStateDescription>
              {t("contracts.contractsList.emptyState.description")}
            </EmptyStateDescription>
            <Button onClick={() => setShowNewContractModal(true)}>
              {t("contracts.contractsList.emptyState.newContractButton")}
            </Button>
          </EmptyState>
        )}
      </ContractsListContent>

      <NewContractModal
        isOpen={showNewContractModal}
        onClose={() => setShowNewContractModal(false)}
        onCreated={(contractId) => {
          setShowNewContractModal(false);
          navigate(`/contracts/${contractId}`);
        }}
      />

      <Modal isOpen={!!contractToArchive} onClose={() => setContractToArchive(null)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%', maxWidth: '500px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', margin: 0 }}>
            {contractToArchive?.nextStatus === CONTRACT_STATUS_ARCHIVED
              ? t("contracts.contractsList.archiveConfirm.archiveTitle")
              : t("contracts.contractsList.archiveConfirm.restoreTitle")}
          </h2>
          <p style={{ color: '#6B7280', lineHeight: 1.6, margin: 0 }}>
            {contractToArchive?.nextStatus === CONTRACT_STATUS_ARCHIVED ? (
              <Trans
                i18nKey="contracts.contractsList.archiveConfirm.archiveMessage"
                values={{ title: contractToArchive?.title }}
                components={{ 1: <strong /> }}
              />
            ) : (
              <Trans
                i18nKey="contracts.contractsList.archiveConfirm.restoreMessage"
                values={{ title: contractToArchive?.title }}
                components={{ 1: <strong /> }}
              />
            )}
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
            <Button
              onClick={handleConfirmArchiveToggle}
              style={contractToArchive?.nextStatus === CONTRACT_STATUS_ARCHIVED ? { backgroundColor: '#ef4444', color: '#ffffff', width: '100%' } : { width: '100%' }}
            >
              {contractToArchive?.nextStatus === CONTRACT_STATUS_ARCHIVED
                ? t("contracts.contractsList.archiveConfirm.archiveButton")
                : t("contracts.contractsList.archiveConfirm.restoreButton")}
            </Button>
            <p
              onClick={() => setContractToArchive(null)}
              style={{ textAlign: 'center', color: '#6B7280', cursor: 'pointer', margin: 0, padding: '0.5rem 0' }}
            >
              {t("contracts.contractsList.archiveConfirm.cancelButton")}
            </p>
          </div>
        </div>
      </Modal>
    </ContractsListWrapper>
  );
};
